import * as React from 'react';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface OpcaoSeletor {
  valor: string;
  rotulo: string;
  auxiliar?: string | undefined;
}

export interface PropsSeletor {
  valor: string;
  aoMudar: (valor: string) => void;
  opcoes: readonly OpcaoSeletor[];
  rotuloAcessivel: string;
  prefixo?: React.ReactNode | undefined;
  className?: string | undefined;
  desabilitado?: boolean | undefined;
  /** Texto quando nada foi escolhido. */
  vazio?: string | undefined;
}

export function Seletor({
  valor,
  aoMudar,
  opcoes,
  rotuloAcessivel,
  prefixo,
  className,
  desabilitado,
  vazio,
}: PropsSeletor): JSX.Element {
  return (
    // `disabled` do Radix não aceita undefined sob exactOptionalPropertyTypes.
    <Select.Root value={valor} onValueChange={aoMudar} disabled={desabilitado ?? false}>
      <Select.Trigger
        aria-label={rotuloAcessivel}
        className={cn(
          'inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-borda bg-superficie px-3.5 text-[13px] font-medium shadow-xs',
          'transition-colors hover:border-bordaForte hover:bg-superficie2 disabled:opacity-60',
          'data-[state=open]:border-primaria data-[state=open]:ring-4 data-[state=open]:ring-primaria/10',
          className,
        )}
      >
        {prefixo ? <span className="text-textoFraco">{prefixo}</span> : null}
        <Select.Value placeholder={vazio ? <span className="font-normal text-textoSuave">{vazio}</span> : undefined} />
        <Select.Icon>
          <ChevronDown className="h-4 w-4 text-textoFraco" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-[60vh] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-borda bg-superficie shadow-forte data-[state=open]:animate-escalar"
        >
          <Select.Viewport className="p-1.5">
            {opcoes.map((opcao) => (
              <Select.Item
                key={opcao.valor}
                value={opcao.valor}
                className="flex min-h-[40px] cursor-pointer select-none items-center gap-2 rounded-lg px-2 text-[13px] font-medium data-[highlighted]:bg-superficie2 data-[highlighted]:outline-none"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <Select.ItemIndicator>
                    <Check className="h-4 w-4 text-primaria" />
                  </Select.ItemIndicator>
                </span>
                <Select.ItemText>{opcao.rotulo}</Select.ItemText>
                {opcao.auxiliar ? (
                  <span className="ml-auto text-xs text-textoFraco">{opcao.auxiliar}</span>
                ) : null}
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
